import type {
  ComputedRef,
  InjectionKey,
} from 'vue';
import {
  computed,
  inject,
  provide,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';

import type { ConversationResultDTO } from '@nora-monorepo/nora-api-client';
import { useToastStore } from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { chatCan } from '#nora-chat-ui/utils/permission';

export interface ConversationDetailsModalContext {
  openDetails: (conversationId: string) => Promise<void>;
}

/**
 * This thingy here represents behavior and state that any ConversationDetailsModalProvider must
 * have.
 *
 * As you can see, it includes among other things:
 *
 * - `conversation`: the actual conversation state for that modal
 * - `isOpen`: whether the modal should be opened or not
 * - `readOnly`: whether the modal is read-only (whether the user has the required permissions to edit the conversation)
 *
 * It also provides the `close` function to close the modal.
 */
export interface ConversationDetailsModalProvider {
  close: () => void;
  conversation: ComputedRef<ConversationResultDTO | undefined>;
  isOpen: ComputedRef<boolean>;
  readOnly: ComputedRef<boolean>;
}

// See vue provide/inject if you are unsure of this.
// We use Symbol() to ensure a unique injection key.
// TODO?: Should probably move all Symbol definition to a single place, so we can see all the injection keys in one place.
const conversationDetailsModalKey: InjectionKey<ConversationDetailsModalContext>
  = Symbol('conversationDetailsModal');

/**
 * You should use this when children of a ConversationDetailsModalProvider need to be able to
 * open the modal.
 */
export function useConversationDetailsModal(): ConversationDetailsModalContext {
  const context = inject(conversationDetailsModalKey);

  if (!context) {
    throw new Error('Conversation details modal provider is missing');
  }

  return context;
}

/**
 * This is the modal provider. It provides the state and behavior for the modal, and should be
 * owned by the component that has the ConversationModal component in its component tree.
 *
 * When used in a parent component, it gives all children with the `openDetails` function to open
 * the modal with a specific conversation.
 */
export function useConversationDetailsModalProvider(): ConversationDetailsModalProvider {
  const conversationStore = useConversationStore();
  const toastStore = useToastStore();
  const { t } = useI18n();
  const selectedConversationId = ref<string>();

  const conversation = computed(() => {
    if (!selectedConversationId.value) {
      return undefined;
    }

    return conversationStore.getConversation(
      selectedConversationId.value,
    )?.conversation;
  });

  const isOpen = computed(() => {
    return selectedConversationId.value !== undefined;
  });

  const readOnly = computed(() => {
    if (!selectedConversationId.value) {
      return false;
    }

    return !chatCan(
      'update:chat.conversation',
      selectedConversationId.value,
    );
  });

  async function openDetails(conversationId: string): Promise<void> {
    try {
      await conversationStore.loadConversation(conversationId, false);

      const loadedConversation = conversationStore.getConversation(
        conversationId,
      )?.conversation;

      if (!loadedConversation) {
        throw new Error('Could not load conversation details');
      }

      selectedConversationId.value = conversationId;
    }
    catch (error) {
      console.error(error);
      toastStore.addError({
        message: t('errors.loadConversationDetailsError'),
      });
    }
  }

  function close(): void {
    const conversationId = selectedConversationId.value;

    selectedConversationId.value = undefined;

    if (conversationId) {
      conversationStore.unloadConversation(conversationId);
    }
  }

  provide(conversationDetailsModalKey, {
    openDetails,
  });

  return {
    close,
    conversation,
    isOpen,
    readOnly,
  };
}
